import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Instagram } from 'lucide-react'
import { BOOKING_URL, business, hours } from '../../data/site'
import { servicePages } from '../../data/servicePages'
import { conditions } from '../../data/conditions'

const exploreLinks = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  { name: 'Services & Pricing', to: '/services' },
  { name: 'Conditions We Treat', to: '/conditions' },
  { name: 'FAQ', to: '/faq' },
  { name: 'Plan Your Visit', to: '/visit' },
  { name: 'Contact', to: '/contact' },
]

const FORMSPREE_ENDPOINT = `https://formspree.io/f/${
  import.meta.env.VITE_FORMSPREE_ID || 'mdareroq'
}`

const headingClass =
  'font-sans text-cream-100 text-xs tracking-[0.2em] uppercase mb-4'
const linkClass =
  'font-sans text-cream-200/70 text-sm hover:text-cream-100 transition-colors'

const LinkColumn = ({ title, links }) => (
  <div>
    <h4 className={headingClass}>{title}</h4>
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className={linkClass}>
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
)

const Footer = () => {
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          _subject: 'New newsletter signup',
        }),
      })

      if (response.ok) {
        setIsSubmitted(true)
        setEmail('')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } catch {
      setError('Could not subscribe right now. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <footer className="bg-dark-600 text-cream-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 md:gap-12 mb-12">
          {/* Brand + newsletter */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="font-display text-2xl text-cream-100 mb-3">
              {business.name}
            </h3>
            <p className="font-sans text-cream-200/70 text-sm leading-relaxed mb-6">
              {business.tagline}
            </p>

            <h4 className={headingClass}>Stay in touch</h4>
            {isSubmitted ? (
              <p className="font-sans text-brand-green-300 text-sm mb-5">
                Thanks — we&apos;ll be in touch.
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="mb-6">
                <div className="flex items-end gap-3 border-b border-cream-100/30 focus-within:border-brand-orange-400 transition-colors">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your email"
                    required
                    className="flex-1 min-w-0 bg-transparent py-2 font-sans text-cream-100 text-sm placeholder:text-cream-100/30 focus:outline-none"
                    aria-label="Email address for newsletter"
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="pb-2 font-sans text-brand-orange-400 text-sm hover:text-brand-orange-300 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '...' : 'Send →'}
                  </button>
                </div>
                {error && (
                  <p className="font-sans text-red-300 text-xs mt-2">{error}</p>
                )}
              </form>
            )}

            <a
              href={business.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-9 h-9 rounded-full border border-cream-100/20 flex items-center justify-center text-cream-100/70 hover:text-cream-100 hover:border-brand-orange-400 transition-all"
            >
              <Instagram className="w-4 h-4" />
            </a>
          </div>

          <LinkColumn title="Explore" links={exploreLinks} />

          <LinkColumn
            title="Services"
            links={servicePages.map((s) => ({ name: s.name, to: `/services/${s.slug}` }))}
          />

          <LinkColumn
            title="Conditions"
            links={conditions.map((c) => ({ name: c.name, to: `/conditions/${c.slug}` }))}
          />

          {/* Visit */}
          <div>
            <h4 className={headingClass}>Visit</h4>
            <address className="not-italic font-sans text-cream-200/70 text-sm leading-relaxed mb-3">
              {business.street}
              <br />
              {business.city}, {business.region} {business.postalCode}
            </address>
            <p className="font-sans text-cream-200/70 text-sm mb-3">
              {hours
                .filter((h) => h.time !== 'Closed')
                .map((h) => (
                  <span key={h.days} className="block">
                    {h.days}, {h.time}
                  </span>
                ))}
            </p>
            <a href={`mailto:${business.email}`} className={`${linkClass} block`}>
              {business.email}
            </a>
            <a href={business.phoneHref} className={`${linkClass} block mb-4`}>
              {business.phoneDisplay}
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-brand-orange-400 text-sm hover:text-brand-orange-300 transition-colors"
            >
              Book online →
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-cream-100/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <p className="font-sans text-cream-100/50 text-xs">
            &copy; {new Date().getFullYear()} {business.name} — all rights reserved
          </p>
          <p className="font-sans text-cream-100/50 text-xs">
            Terms · Privacy
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
